<template>
  <ValidationObserver
    ref="observer"
    v-slot="{ valid, dirty }"
  >
    <div class="boilerOperatingParameters">
      <!-- // 1ere partie : information générales -->
      <v-row>
        <v-col
          cols="12"
          md="8"
        >
          <VTextField
            v-model.number="boilerOperatingParameters.boilerRatedCapacity.value"
            :label="$t('massBalance.e4Form.boilerOperatingParameters.boilerRatedCapacity')"
            outlined
            :rules="{ required: true }"
            type="number"
            formatNumber
            @input="onInput"
          />
        </v-col>
        <v-col
          cols="12"
          md="4"
        >
          <VSelect
            v-model="boilerOperatingParameters.boilerRatedCapacity.unit"
            :items="boilerRatedCapacityUnits"
            :label="$t('massBalance.e4Form.boilerOperatingParameters.unit')"
            outlined
            :rules="{ required: true, min: 1, max: 20 }"
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col
          cols="12"
          md="8"
        >
          <VTextField
            v-model.number="boilerOperatingParameters.feedWaterTemperature.value"
            :label="$t('massBalance.e4Form.boilerOperatingParameters.feedWaterTemperature')"
            outlined
            :rules="{ required: true }"
            formatNumber
            type="number"
          />
        </v-col>
        <v-col
          cols="12"
          md="4"
        >
          <VSelect
            v-model="boilerOperatingParameters.feedWaterTemperature.unit"
            :items="feedWaterTemperatureUnits"
            :label="$t('massBalance.e4Form.boilerOperatingParameters.unit')"
            outlined
            :rules="{ required: true, min: 1, max: 20 }"
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col
          cols="12"
          md="8"
        >
          <VTextField
            v-model.number="boilerOperatingParameters.operatingPressure.value"
            :label="$t('massBalance.e4Form.boilerOperatingParameters.operatingPressure')"
            outlined
            formatNumber
            type="number"
            :rules="{ required: true }"
          />
        </v-col>
        <v-col
          cols="12"
          md="4"
        >
          <VSelect
            v-model="boilerOperatingParameters.operatingPressure.unit"
            :items="operatingPressureUnits"
            :label="$t('massBalance.e4Form.boilerOperatingParameters.unit')"
            outlined
            :rules="{ required: true, min: 1, max: 20 }"
          />
        </v-col>
      </v-row>
      <div style="font-weight: 600;">
        {{ $t('massBalance.selectEnergyBalanceType') }}
      </div>
      <v-radio-group
        v-model="boilerOperatingParameters.energyBalanceType"
        row
        @change="onChangeEnergyBalanceType"
      >
        <v-radio
          v-for="(item, index) in balanceTypeEnergiesList"
          :key="index"
          :label="$t(`massBalance.e4Form.boilerOperatingParameters.${item}`)"
          :value="item"
          :rules="{ required: true }"
        />
      </v-radio-group>

      <!-- // 2eme partie : les combustibles -->
      <div
        v-if="boilerOperatingParameters.energyBalanceType"
      >
        <div
          style="margin-bottom: 20px; color: #26384B; font-weight: bold; "
        >
          {{ $t('massBalance.mixEnergetique') }}
          <span
            class="boilerOperatingParameters__vignette"
          > 5 {{ $t('measures.labels.maximum') }} </span>
        </div>
        <div
          v-for="(fuel, index) in boilerOperatingParameters.fuels"
          :key="index"
          style="margin-left: -15px"
        >
          <div
            style="display: flex; flex-direction: row; align-items: center;"
          >
            <v-icon
              large
              color="primary"
              @click="actionArrow(index)"
            >
              {{ index === combustibleSelected ? 'mdi-menu-down' : 'mdi-menu-right' }}
            </v-icon>
            <div style="font-weight: 800">
              {{ $t('massBalance.combustible') }} {{ index + 1 }}
            </div>
            <div>
              <span
                class="boilerOperatingParameters__vignette"
                style="border-radius: 0; font-weight: 800; padding-left: 15px;
                padding-right: 15px; margin-left: 20px; padding-top: 0px; padding-bottom: 0px;"
              > {{ boilerOperatingParameters.fuels[index].percentage || 0 }} % </span>
            </div>
          </div>

          <div
            v-if="index === combustibleSelected"
          >
            <v-row
              v-if="boilerOperatingParameters.energyBalanceType === E4_FUEL"
            >
              <v-col
                cols="12"
                md="5"
              >
                <VSelect
                  v-model="boilerOperatingParameters.fuels[index].network"
                  :items="networksList"
                  :label="$t('massBalance.e4Form.boilerOperatingParameters.network')"
                  outlined
                  :rules="{ required: true }"
                  @input="onChangeNetwork(boilerOperatingParameters.fuels[index].network, index)"
                />
              </v-col>
              <v-col
                cols="12"
                md="5"
              >
                <div>
                  <VSelect
                    v-model="boilerOperatingParameters.fuels[index].component"
                    :items="boilerOperatingParameters.fuels[index].componentList"
                    :label="$t('massBalance.e4Form.boilerOperatingParameters.component')"
                    :disabled="boilerOperatingParameters.fuels[index].network === null"
                    outlined
                    :rules="{ required: true }"
                    @input="onChangeComponent(boilerOperatingParameters.fuels[index].component ,index)"
                  />
                </div>
              </v-col>
              <v-col
                cols="12"
                md="2"
              >
                <div>
                  <VSelect
                    v-model="boilerOperatingParameters.fuels[index].analysisConfiguration"
                    :items="boilerOperatingParameters.fuels[index].analysisList"
                    :label="$t('massBalance.e4Form.boilerOperatingParameters.analysis')"
                    outlined
                    :rules="{ required: true }"
                    :disabled="boilerOperatingParameters.fuels[index].component === null "
                    itemValue="id"
                    return-object
                    @change="onChangeAnalysisConfiguration(index)"
                  />
                </div>
              </v-col>
            </v-row>
            <v-row>
              <v-col
                cols="12"
                md="5"
              >
                <VSelect
                  v-model="boilerOperatingParameters.fuels[index].fuelType"
                  :items="fuelTypeList"
                  :label="$t('massBalance.e4Form.boilerOperatingParameters.fuelType')"
                  outlined
                  :rules="{ required: true }"
                  disabled
                  @change="onInput"
                />
              </v-col>
              <v-col
                cols="12"
                md="5"
              >
                <VAutocomplete
                  v-model="boilerOperatingParameters.fuels[index].fuelName"
                  :items="getFuelNameListByFuelType(boilerOperatingParameters.fuels[index].fuelType)"
                  itemValue="id"
                  itemText="name"
                  disabled
                  :label="$t('massBalance.e4Form.boilerOperatingParameters.fuelName')"
                  outlined
                  :rules="[{ required: true }]"
                  @change="onInput"
                />
              </v-col>
              <v-col
                cols="12"
                md="2"
              >
                <VSelect
                  v-if="boilerOperatingParameters.energyBalanceType === E4_FUEL"
                  v-model="boilerOperatingParameters.fuels[index].unit"
                  disabled
                  :items="getCombustilbleUnitByFuelType(
                    boilerOperatingParameters.fuels[index].fuelType,
                    boilerOperatingParameters.fuels[index].fuelName
                  )"
                  :label="$t('massBalance.e4Form.boilerOperatingParameters.unit')"
                  outlined
                  :rules="{ required: true, min: 1, max: 20 }"
                  @change="onInput"
                  @input="onChangeUnit(index)"
                />
              </v-col>
            </v-row>
            <v-row
              v-if="conditionDensity(index)"
            >
              <v-col
                cols="12"
                md="5"
              >
                <VTextField
                  v-model.number="boilerOperatingParameters.fuels[index].density.value"
                  :label="$t('forms.labels.density')"
                  outlined
                  @change="onInput"
                />
              </v-col>

              <v-col
                cols="12"
                md="5"
              >
                <VSelect
                  v-model="boilerOperatingParameters.fuels[index].density.unit"
                  :label="$t('forms.labels.densityUnit')"
                  :items="densitiesUnits"
                  outlined
                  @change="onInput"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col
                cols="12"
                md="5"
              >
                <VTextField
                  v-model.number="boilerOperatingParameters.fuels[index].percentage"
                  :label="$t('massBalance.e4Form.boilerOperatingParameters.total%')"
                  outlined
                  :rules="{ required: true, min: 0, max: 100}"
                  type="number"
                  @input="onInput"
                />
                <div style="color:#26384B">
                  {{ $t('massBalance.totalMessage') }}
                </div>
              </v-col>
            </v-row>
            <div
              v-if="boilerOperatingParameters.fuels.length > 1"
              class="delStyleContent"
            >
              <div class="delStyleContentDel">
                <v-icon
                  color="white"
                  size="20"
                  @click="removeCombustibleData(index)"
                >
                  delete
                </v-icon>
              </div>
              {{ $t('legionellaChart.deleteIcon') }}
            </div>
          </div>
        </div>

        <div
          v-if="boilerOperatingParameters.fuels.length < 5"
          class="actionStyle"
        >
          <div
            class="subBarre"
          />
          <div
            class="addStyle"
            @click="()=>addCombustibleData()"
          >
            <div class="addStyleContent">
              <v-icon
                color="white"
                size="20"
              >
                add
              </v-icon>
            </div>
            <div>
              {{ $t('massBalance.add') }}
            </div>
          </div>
        </div>
        <div>
          <div
            style="display: flex; align-items: center; width: 100%; height: 100%"
          >
            <img
              :src="totalPercentageCheck
                ? require('@/assets/images/icon_validate_square.svg')
                : require('@/assets/images/icon_warning_square.svg')"
              alt="Aquassist"
              height="58"
              style="flex-shrink: 0;"
            >
            <div
              class="boilerOperatingParameters__boxAlert"
              :style="{ color: totalPercentageCheck ? 'green' : 'red' }"
            >
              <span
                style="font-weight: bold"
              >
                {{ !totalPercentageCheck ? $t('massBalance.e4Form.alerts.warning') : $t('massBalance.e4Form.alerts.success') }}
              </span>
              <span>
                {{ totalPercentageCheck
                  ? $t('massBalance.e4Form.alerts.exact')
                  : $t('massBalance.e4Form.alerts.inexact') }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <v-dialog
        v-model="dialogVisible"
        persistent
        maxWidth="600"
      >
        <v-card>
          <v-card-title class="headline">
            {{ $t('massBalance.changeEnergyBalanceWarningTitle') }}
          </v-card-title>
          <v-card-text>
            {{ $t('massBalance.changeEnergyBalanceWarning') }}
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              color="primary"
              text
              @click="confirmDialog"
            >
              {{ $t('massBalance.changeEnergyBalanceWarningConfirm') }}
            </v-btn>
            <v-btn
              color="secondary"
              text
              @click="cancelDialog"
            >
              {{ $t('massBalance.changeEnergyBalanceWarningCancel') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <SlotScopeWatcher
        :value="dirty"
        @change="$emit('dirty-changed', dirty)"
      />
      <SlotScopeWatcher
        :value="valid"
        @change="$emit('valid-changed', valid)"
      />
    </div>
  </ValidationObserver>
</template>

<script>
import { ValidationObserver } from 'vee-validate';
import SlotScopeWatcher from '@/components/shared/widgets/forms/validationHelper/SlotScopeWatcher';
import VTextField from '@/components/shared/widgets/forms/VuetifyInputs/VTextField.vue';
import VSelect from '@/components/shared/widgets/forms/VuetifyInputs/VSelect.vue';
import { mapGetters, mapActions } from 'vuex';
import { FUEL_CONSUMPTION_ANALYSIS_TYPE } from '@/aquassist-shared/constants/types/componentAnalysisConfiguration';
import { STEAM } from '@/aquassist-shared/constants/types/network';
import {
    BALANCETYPEENERGY,
    BOILER_RATED_CAPACITY_UNITS,
    FEED_WATER_TEMPERATURE_UNITS,
    OPERATING_PRESSURE_UNITS,
    COMBUSTILES_LISTS,
    FUELNAMES,
    CONBUSTIBLES_UNITS,
    US_STATES_GRID_LIST,
    E4_FUEL,
    E4_BOILER,
    DENSITIES_UNITS,
    BIOMASS_UNITS_FOR_FUEL_NAME_EXEPTION,
    UNITS_WITH_DENTISITY,
} from '@/aquassist-shared/constants/massBalance';

export default {
    name: 'BoilerOperatingParameters',
    components: {
        VTextField,
        VSelect,
        ValidationObserver,
        SlotScopeWatcher,
    },
    props: {
        editMode: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            dialogVisible: false,
            localEnergyBalanceType: null,
            boilerOperatingParameters: {
                boilerRatedCapacity: {
                    value: null,
                    unit: null,
                },
                feedWaterTemperature: {
                    value: null,
                    unit: null,
                },
                operatingPressure: {
                    value: null,
                    unit: null,
                },
                energyBalanceType: null,
                fuels: [
                    {
                        fuelType: null,
                        fuelName: null,
                        unit: null,
                        network: null,
                        component: null,
                        analysisConfiguration: null,
                        percentage: null,
                        // Facultatif
                        density: {
                            unit: null,
                            value: null,
                        },
                        _id: null,
                        fuelConsumption: [],
                        // Temporary data
                        componentsList: [],
                        analysisList: [],
                    },
                ],
            },
            combustibleSelected: 0,
            modelFuelEmpty: {
                fuelType: null,
                fuelName: null,
                unit: null,
                network: null,
                component: null,
                analysis: null,
                percentage: null,
                fuelConsumption: [],
                density: {
                    unit: null,
                    value: null,
                },
                // Temporary data
                componentsList: [],
                analysisList: [],
            },
            fuelNameListCopy: { ...FUELNAMES },
            networksList: [],
            isInitialized: false,
        };
    },

    computed: {
        ...mapGetters([
            'activeTab',
            'site',
            'userLang',
            'e4Config',
        ]),
        newActiveTab: {
            get() {
                return this.activeTab;
            },
            set(newValue) {
                this.$store.dispatch('changeActiveTab', newValue);
            },
        },
        balanceTypeEnergiesList() {
            return Object.values(BALANCETYPEENERGY || {});
        },
        boilerRatedCapacityUnits() {
            return Object.values(BOILER_RATED_CAPACITY_UNITS || {});
        },
        feedWaterTemperatureUnits() {
            return Object.values(FEED_WATER_TEMPERATURE_UNITS || {});
        },
        operatingPressureUnits() {
            return Object.values(OPERATING_PRESSURE_UNITS || {});
        },
        totalPercentageCheck() {
            return this.percentageTotal === 100;
        },
        fuelTypeList() {
            return Object.values(COMBUSTILES_LISTS || {}).map(item => ({
                id: item,
                name: this.$t(`massBalance.e4Form.fuelTypes.${item}`),
            }));
        },
        fuelNameList() {
            return FUELNAMES || [];
        },
        conbustiblesUnitsList() {
            return CONBUSTIBLES_UNITS || [];
        },
        usStatesGridList() {
            return US_STATES_GRID_LIST;
        },
        E4_FUEL() {
            return E4_FUEL;
        },
        E4_BOILER() {
            return E4_BOILER;
        },
        densitiesUnits() {
            return DENSITIES_UNITS;
        },
        biomassUnitsForFuelNameExeption() {
            return Object.values(BIOMASS_UNITS_FOR_FUEL_NAME_EXEPTION || {}).map(item => ({
                id: item,
                name: this.$t(`massBalance.e4Form.fuelTypes.${item}`),
            }));
        },
        percentageTotal() {
            let total = 0;
            this.boilerOperatingParameters.fuels.forEach(fuel => {
                total += Number(fuel.percentage);
            });
            return Number(total);
        },
        unitsWithDentities() {
            return UNITS_WITH_DENTISITY;
        },

    },
    watch: {
        boilerOperatingParameters: {
            immediate: true,
            deep: true,
            handler() {
                this.onInput();
            },
        },
        // eslint-disable-next-line func-names
        'boilerOperatingParameters.energyBalanceType': {
            handler(_newVal) {
                this.localEnergyBalanceType = _newVal;
                this.onInput();
            },
            immediate: true,
            deep: true,
        },
    },
    async created() {
        if (this.editMode) {
            this.boilerOperatingParameters = { ...this.e4Config.boilerOperatingParameters };

            const networkPromises = this.boilerOperatingParameters.fuels
                .filter(fuel => fuel.network)
                .map((fuel, index) => this.getComponentsListByNetwork(fuel.network, index));

            const componentPromises = this.boilerOperatingParameters.fuels
                .filter(fuel => fuel.component)
                .map((fuel, index) => this.getAnalysisListByComponent(fuel.component, index));

            await Promise.all(networkPromises);
            await Promise.all(componentPromises);

            this.checkFormular();
        }

        await this.getNetworks();
        this.isInitialized = true;
    },

    mounted() {
        this.refreshKey += 1;
    },

    methods: {
        ...mapActions({
            setE4Config: 'setE4Config',
            resetE4Fuels: 'resetE4Fuels',
        }),

        async getNetworks() {
            const { data } = await this.$rq.getAllSiteNetwork(this.site.id, STEAM.toLowerCase());
            this.networksList = data;
        },
        async onInput() {
            if (!this.isInitialized) return;
            this.setE4Config({ boilerOperatingParameters: this.boilerOperatingParameters });
            this.checkFormular();
        },
        actionArrow(index) {
            if (this.combustibleSelected !== index) {
                this.combustibleSelected = index;
            } else {
                this.combustibleSelected = null;
            }
        },
        removeCombustibleData(index) {
            this.boilerOperatingParameters.fuels.splice(index, 1);
            this.combustibleSelected = null;
        },
        addCombustibleData() {
            const newFuel = JSON.parse(JSON.stringify(this.modelFuelEmpty)); // Copie profonde
            this.boilerOperatingParameters.fuels.push(newFuel);
            this.combustibleSelected = null;
        },
        getFuelNameListByFuelType(fuelType) {
            return Object.values(this.fuelNameList[fuelType] || {}).map(item => ({
                id: item,
                name: this.$t(`massBalance.e4Form.fullName.${fuelType}.${item}`),
            }));
        },
        getCombustilbleUnitByFuelType(fuelType, fuelName) {
            if (fuelType === 'biomass' && BIOMASS_UNITS_FOR_FUEL_NAME_EXEPTION.includes(fuelName)) {
                return Object.values(this.conbustiblesUnitsList.gasFossil || {}).map(item => ({
                    id: item,
                    name: this.$t(`massBalance.e4Form.units.${item}`),
                }));
            }
            return Object.values(this.conbustiblesUnitsList[fuelType] || {}).map(item => ({
                id: item,
                name: this.$t(`massBalance.e4Form.units.${item}`),
            }));
        },
        onChangeEnergyBalanceType(arg1) {
            if (!this.boilerOperatingParameters.energyBalanceType) {
                this.boilerOperatingParameters.fuels = [];
            }
            if (arg1 === 'BOILER') {
                this.dialogVisible = true;
            }
            this.onInput();
        },
        confirmDialog() {
            this.resetE4Fuels();
            this.dialogVisible = false;
        },
        cancelDialog() {
            this.dialogVisible = false;
            // Rétablir l'ancienne valeur
            this.boilerOperatingParameters.energyBalanceType = this.localEnergyBalanceType === 'BOILER' ? 'FUEL' : 'BOILER';
        },
        async checkFormular() {
            const formularOK = await this.$refs.observer.validate();
            if (this.percentageTotal !== 100 || !formularOK || this.boilerOperatingParameters.energyBalanceType === null) {
                await this.$refs.observer.reset();
            }
            if (this.percentageTotal === 100 && formularOK && this.boilerOperatingParameters.energyBalanceType !== null) {
                this.$refs.observer.reset();

                this.$refs.observer.validate();
            }
        },
        conditionDensity(index) {
            if (
                this.boilerOperatingParameters.energyBalanceType === E4_FUEL
                && this.unitsWithDentities.includes(this.boilerOperatingParameters.fuels[index].unit)
            ) { return true; }

            return false;
        },
        async getComponentsListByNetwork(networkId, index) {
            try {
                const componentsList = await this.getComponentListByNetwork(networkId);
                this.boilerOperatingParameters.fuels[index].componentList = componentsList;
            } catch (error) {
                console.error('Error fetching components list:', error);
            }
        },

        async getComponentListByNetwork(networkId) {
            if (networkId !== null) {
                const { data } = await this.$rq.getComponentsListByNetworkId(this.site.id, networkId);
                return data;
            }
            return [];
        },

        onChangeNetwork(networkId, index) {
            this.boilerOperatingParameters.fuels[index].component = null;
            this.boilerOperatingParameters.fuels[index].analysis = null;
            this.getComponentsListByNetwork(networkId, index);
        },

        onChangeComponent(componentId, index) {
            this.boilerOperatingParameters.fuels[index].analysis = null;
            this.getAnalysisListByComponent(componentId, index);
        },


        async getAnalysisListByComponent(componentId, index) {
            try {
                const analysisList = await this.getConfigurationAnalysisType(componentId, index);
                this.boilerOperatingParameters.fuels[index].analysisList = analysisList;
            } catch (error) {
                console.error('Error fetching components list:', error);
            }
        },


        async getConfigurationAnalysisType(componentId) {
            const { data } = await this.$rq.getComponentConfigurations(componentId);
            const extractedData = data.configurations
                .filter(item => item.analysisType?.type === FUEL_CONSUMPTION_ANALYSIS_TYPE && item.fuelMeter)
                .map(item => ({
                    id: item.id,
                    name: item.analysisType?.name[this.userLang] || '',
                    density: item.fuelMeter?.density,
                    unit: item.fuelMeter?.measurementUnit,
                    fuelType: item.fuelMeter?.fuelType,
                    fuelName: item.fuelMeter?.fuelName,
                }));

            return extractedData;
        },

        onChangeUnit(index) {
            this.boilerOperatingParameters.fuels[index].density.value = null;
            this.boilerOperatingParameters.fuels[index].density.unit = null;
        },
        onChangeAnalysisConfiguration(index) {
            const analysisObj = this.boilerOperatingParameters.fuels[index].analysisConfiguration;
            if (analysisObj) {
                this.boilerOperatingParameters.fuels[index].density.value = analysisObj.density.value || null;
                this.boilerOperatingParameters.fuels[index].density.unit = analysisObj.density.unit || null;
                this.boilerOperatingParameters.fuels[index].unit = analysisObj.unit || null;
                this.boilerOperatingParameters.fuels[index].fuelType = analysisObj.fuelType || null;
                this.boilerOperatingParameters.fuels[index].fuelName = analysisObj.fuelName || null;
                this.boilerOperatingParameters.fuels[index].analysisConfiguration = analysisObj.id;
            }
            this.onInput();
        },
    },
};

</script>

<style scoped lang="scss">

  .boilerOperatingParameters {
    &__vignette {
      background-color: #e9e5e5;
      padding-left: 10px;
      padding-right: 10px;
      padding-top: 5px;
      padding-bottom: 5px;
      border-radius: 20px;
      margin-left: 10px;
      font-weight: bold;
    }
    &__boxAlert{
      flex-grow: 1;
      padding-left: 20px;
      box-shadow: 2px 1px 2px 2px rgb(0 0 0 / 20%);
      height: 54px;
      border: 0px solid #888888;

      display: flex;
      flex-direction: column;
      justify-content: center;

    }
  }
</style>
